import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import theme from '../theme';
import config from '../config';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { getBackNavigationPath } from '../utils/adminUtils';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  will-change: opacity;
`;

const PopupContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: ${props => props.theme.borderRadius.large};
  padding: 48px 32px 32px 32px;
  max-width: 400px;
  max-height: 85vh;
  overflow-y: auto;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(205, 255, 155, 0.3);
  position: relative;
  will-change: opacity, transform;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(219, 39, 98, 0.3);

  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PopupTitle = styled.h2`
  font-size: 26px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 18px;
  text-align: center;
  font-weight: 700;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    top: 38px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, ${props => props.theme.colors.accent1} 50%, transparent 100%);
  }
`;

const SwitchRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const SwitchButton = styled.button`
  background: ${props => props.active ? theme.colors.secondary : '#f0f0f0'};
  color: ${props => props.active ? 'white' : theme.colors.primary};
  border: none;
  border-radius: 20px;
  padding: 8px 22px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
`;

const ErrorMsg = styled.div`
  color: #e8316d;
  font-size: 14px;
  text-align: center;
  margin-top: 4px;
`;

const SuccessMsg = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-size: 15px;
  text-align: center;
  margin-top: 4px;
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  display: block;
  padding: 14px 32px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }
`;

const SubmitButton = styled(ActionButton)`
  margin: 12px 0 0 0;
  width: 100%;
  padding: 12px 0;
`;

const LoginPopup = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot-password', or 'forgot-username'
  const [form, setForm] = useState({ username: '', password: '', email: '', additionalInfo: '', otp: '', newPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [forgotUsernameSuccess, setForgotUsernameSuccess] = useState(false);
  const { login: authLogin, hasRole } = useAuth();
  const navigate = useNavigate();

  // Reset popup to default login state when it closes
  useEffect(() => {
    if (!isOpen) {
      setMode('login');
      setForm({ username: '', password: '', email: '', additionalInfo: '', otp: '', newPassword: '' });
      setError('');
      setSuccess('');
      setOtpSent(false);
      setLoading(false);
      setPasswordResetSuccess(false);
      setForgotUsernameSuccess(false);
    }
  }, [isOpen]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSwitch = newMode => {
    setMode(newMode);
    setForm({ username: '', password: '', email: '', additionalInfo: '', otp: '', newPassword: '' });
    setError('');
    setSuccess('');
    setOtpSent(false);
    setPasswordResetSuccess(false);
    setForgotUsernameSuccess(false);
  };

  const requestOTP = async () => {
    if (!form.email) {
      setError('Bitte gib deine E-Mail-Adresse ein.');
      return;
    }
    
    setError('');
    setLoading(true);
    try {
      const res = await fetch(config.getApiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email })
      });
      
      if (res.ok) {
        setOtpSent(true);
        setSuccess('Falls ein Nutzer mit dieser E-Mail-Adresse existiert, wurde das Einmalpasswort gesendet.');
      } else {
        const err = await res.text();
        setError(err || 'Fehler beim Senden des Einmalpassworts.');
      }
    } catch (e) {
      setError('Netzwerkfehler.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await fetch(config.getApiUrl('/api/auth/login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: form.username, password: form.password })
        });
        if (res.ok) {
          const data = await res.json();
          setSuccess('');
          authLogin(data.token);
          
          // Check if user is admin and navigate accordingly
          setTimeout(() => {
            onClose();
            navigate(getBackNavigationPath());
          }, 100);
        } else {
          const err = await res.text();
          setError(err || 'Login fehlgeschlagen.');
        }
      } else if (mode === 'register') {
        const res = await fetch(config.getApiUrl('/api/auth/register'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username,
            password: form.password,
            email: form.email,
            firstName: form.firstName,
            additionalInfo: form.additionalInfo
          })
        });
        if (res.ok) {
          setSuccess('Registrierung erfolgreich! Bitte warte auf Freischaltung.');
        } else {
          const err = await res.text();
          setError(err || 'Registrierung fehlgeschlagen.');
        }
      } else if (mode === 'forgot-username') {
        const res = await fetch(config.getApiUrl('/api/auth/forgot-username'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email })
        });
        
        if (res.ok) {
          setSuccess('Falls ein Account mit dieser E-Mail-Adresse existiert, wurde der Benutzername gesendet.');
          setForgotUsernameSuccess(true);
        } else {
          const err = await res.text();
          setError(err || 'Fehler beim Senden des Benutzernamens.');
        }
      } else if (mode === 'forgot-password') {
        if (!otpSent) {
          await requestOTP();
          return;
        }
        
        
        const res = await fetch(config.getApiUrl('/api/auth/reset-password'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: form.email,
            otp: form.otp,
            newPassword: form.newPassword
          })
        });
        
        if (res.ok) {
          setSuccess('Passwort erfolgreich zurückgesetzt! Du kannst dich jetzt anmelden.');
          setPasswordResetSuccess(true);
        } else {
          const err = await res.text();
          setError(err || 'Fehler beim Zurücksetzen des Passworts.');
        }
      }
    } catch (e) {
      setError('Netzwerkfehler.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={onClose}
        >
          <PopupContainer
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={e => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>×</CloseButton>
            <PopupTitle>
              {mode === 'login' ? 'Login' : 
               mode === 'register' ? 'Benutzer erstellen' : 
               mode === 'forgot-password' ? 'Passwort vergessen' : 
               'Nutzername vergess.'}
            </PopupTitle>
            <SwitchRow>
              <SwitchButton active={mode === 'login'} onClick={() => handleSwitch('login')}>Login</SwitchButton>
              <SwitchButton active={mode === 'register'} onClick={() => handleSwitch('register')}>Registrieren</SwitchButton>
            </SwitchRow>
            {success && mode === 'register' ? (
            <div>
              <div style={{
                margin: '32px 0',
                padding: '24px',
                background: '#f5f5f5',
                borderRadius: theme.borderRadius.medium,
                border: '1px solid #e0e0e0',
                textAlign: 'center',
                fontSize: 18,
                color: theme.colors.primary,
                fontWeight: 500
              }}>
                Deine Registrierung wurde erfolgreich übermittelt.<br />
                Wir prüfen deine Angaben und melden uns zeitnah.<br /><br />
                Du kannst dieses Fenster jetzt schließen.
              </div>
              <ActionButton onClick={onClose}>
                Fenster schließen
              </ActionButton>
            </div>
            ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {mode === 'forgot-username' ? (
                <>
                  <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                    E-Mail-Adresse
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                      autoCapitalize="off"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `2px solid ${theme.colors.accent1}`,
                        borderRadius: theme.borderRadius.medium,
                        marginTop: 4,
                        fontSize: 16
                      }}
                    />
                  </label>
                </>
              ) : mode === 'forgot-password' ? (
                <>
                  <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                    E-Mail-Adresse
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                      autoCapitalize="off"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `2px solid ${theme.colors.accent1}`,
                        borderRadius: theme.borderRadius.medium,
                        marginTop: 4,
                        fontSize: 16
                      }}
                    />
                  </label>
                  {otpSent && (
                    <>
                      <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                        Einmalpasswort
                        <input
                          name="otp"
                          type="text"
                          value={form.otp}
                          onChange={handleChange}
                          autoCapitalize="off"
                          required
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: `2px solid ${theme.colors.accent1}`,
                            borderRadius: theme.borderRadius.medium,
                            marginTop: 4,
                            fontSize: 16
                          }}
                        />
                      </label>
                      <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                        Neues Passwort
                        <input
                          name="newPassword"
                          type="password"
                          value={form.newPassword}
                          onChange={handleChange}
                          autoComplete="new-password"
                          autoCapitalize="off"
                          required
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: `2px solid ${theme.colors.accent1}`,
                            borderRadius: theme.borderRadius.medium,
                            marginTop: 4,
                            fontSize: 16
                          }}
                        />
                      </label>
                    </>
                  )}
                </>
              ) : (
                <>
                  <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                    Benutzername
                    <input
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      autoComplete="username"
                      autoCapitalize="off"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `2px solid ${theme.colors.accent1}`,
                        borderRadius: theme.borderRadius.medium,
                        marginTop: 4,
                        fontSize: 16
                      }}
                    />
                  </label>
                  <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                    Passwort
                    <input
                      name="password"
                      type="password"
                      value={form.password}
                      onChange={handleChange}
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      autoCapitalize="off"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `2px solid ${theme.colors.accent1}`,
                        borderRadius: theme.borderRadius.medium,
                        marginTop: 4,
                        fontSize: 16
                      }}
                    />
                  </label>
                </>
              )}
              {mode === 'register' && (
                <>
                  <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                    Vorname
                    <input
                      id="register-firstname"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      autoCapitalize="on"
                      value={form.firstName || ''}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `2px solid ${theme.colors.accent1}`,
                        borderRadius: theme.borderRadius.medium,
                        marginTop: 4,
                        fontSize: 16
                      }}
                    />
                  </label>
                  <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                    Email
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                      autoCapitalize="off"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `2px solid ${theme.colors.accent1}`,
                        borderRadius: theme.borderRadius.medium,
                        marginTop: 4,
                        fontSize: 16
                      }}
                    />
                  </label>
                  <label style={{ fontWeight: 500, color: theme.colors.secondary, marginBottom: 4, marginTop: 8 }}>
                    Zusätzliche Info
                    <textarea
                      name="additionalInfo"
                      value={form.additionalInfo}
                      onChange={handleChange}
                      autoCapitalize="on"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `2px solid ${theme.colors.accent1}`,
                        borderRadius: theme.borderRadius.medium,
                        marginTop: 4,
                        fontSize: 16,
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        minHeight: 60
                      }}
                    />
                  </label>
                </>
              )}
              {mode === 'login' && (
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => handleSwitch('forgot-password')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: theme.colors.secondary,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Passwort vergessen?
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSwitch('forgot-username')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: theme.colors.secondary,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Benutzername vergessen?
                    </button>
                  </div>
                </div>
              )}
              {error && <ErrorMsg>{error}</ErrorMsg>}
              {success && (mode === 'login' || mode === 'forgot-password' || mode === 'forgot-username') && <SuccessMsg>{success}</SuccessMsg>}
              <SubmitButton 
                type={(passwordResetSuccess || forgotUsernameSuccess) ? "button" : "submit"} 
                disabled={loading}
                onClick={(passwordResetSuccess || forgotUsernameSuccess) ? onClose : undefined}
              >
                {loading ? 'Bitte warten...' : 
                 (passwordResetSuccess || forgotUsernameSuccess) ? 'Schließen' :
                 (mode === 'login' ? 'Login' : 
                  mode === 'register' ? 'Registrieren' : 
                  mode === 'forgot-username' ? 'Benutzername senden' :
                  (otpSent ? 'Passwort zurücksetzen' : 'Einmalpasswort anfordern'))}
              </SubmitButton>
            </form>
            )}
          </PopupContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default LoginPopup; 